import React, { useEffect, useState } from 'react';
import { getBrand, getBrandLogo } from 'flow/brands/brands';
import { isGeonexConfigured } from 'app-config';

type LogoTypes = 'logo24' | 'logo48' | 'logo60' | 'versionIcon';

interface BrandLogoProps {
    type: LogoTypes;
    className?: string;
    withEnvironmentBranding?: boolean;
    isPreLogin?: boolean;
}

const BrandLogo = ({
    type,
    className,
    withEnvironmentBranding,
    isPreLogin = false,
}: BrandLogoProps) => {
    const [imageError, setImageError] = useState(false);
    const [themeMode, setThemeMode] = useState(
        document.documentElement.classList.contains('dark')
            ? 'DarkMode'
            : 'LightMode',
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setThemeMode(
                document.documentElement.classList.contains('dark')
                    ? 'DarkMode'
                    : 'LightMode',
            );
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        return () => observer.disconnect();
    }, []);

    const logoType = type === 'versionIcon' ? type : `${type}${themeMode}`;

    /**
     * Use GeoNex for the pre-login logo only until the brand's own backend
     * (brandAdminUrl) is known; after that the logo loads from the brand backend.
     */
    const isBrandBackendKnown = !!getBrand()?.brandAdminUrl;
    const isGeoNexEnabled =
        isGeonexConfigured && isPreLogin && !isBrandBackendKnown;

    const logoUrl = getBrandLogo(
        logoType,
        withEnvironmentBranding,
        isGeoNexEnabled,
    );
    const brandName = getBrand(withEnvironmentBranding)?.brandNameUIFriendly;

    useEffect(() => {
        setImageError(false);
    }, [logoUrl]);

    if (!logoUrl || imageError) {
        return null;
    }

    return (
        <div className={className}>
            <img
                src={logoUrl}
                alt={`${brandName} brand logo`}
                onError={() => setImageError(true)}
            />
        </div>
    );
};

export default React.memo(BrandLogo);
